import React, { useState, useEffect, useRef } from 'react';
import BaseElement from './BaseElement';
import TextEditor from '../modals/TextEditor/TextEditor';
import { generateTextAudio } from '../../../services/audio/generateTextAudio';
import { PlayCircleIcon, PauseCircleIcon, MusicalNoteIcon, ArrowPathIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { notify } from '../../../utils/notify.jsx';
import { useCredits } from '../../../contexts/CreditsContext';
import NarrationConfirmModal from './NarrationConfirmModal';
import { downloadAudioFile } from '../../../services/audio/downloadHelper';

export default function TextElement({
  id,
  content,
  audio,
  position,
  size,
  style,
  zIndex,
  isSelected,
  onSelect,
  onMove,
  onResize,
  onChange,
  onDelete,
  onDuplicate,
  onZIndexChange,
  containerRef,
}) {
  const [showEditor, setShowEditor] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0); // 0..1
  const [elapsedSec, setElapsedSec] = useState(0);
  const audioRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const { checkCredits, updateCredits, credits } = useCredits();
  const [showNarrationConfirm, setShowNarrationConfirm] = useState(false);
  const [isInlineEditing, setIsInlineEditing] = useState(false);
  const [editingContent, setEditingContent] = useState(content);
  const [showDoubleClickHint, setShowDoubleClickHint] = useState(false);
  const [hintPosition, setHintPosition] = useState({ x: 0, y: 0 });
  const hintTimeoutRef = useRef(null);
  const inlineEditRef = useRef(null);
  const isFirstFocusRef = useRef(true);
  const justEnteredEditModeRef = useRef(false);

  // Expose editing state globally so the canvas can hide the center pencil
  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (showEditor) {
        // eslint-disable-next-line no-console
        console.log('[TEXT EDITOR] opening', id);
        window.__sbwEditingSelectedId = id;
        window.dispatchEvent(new CustomEvent('sbw:editing-change', { detail: { id, isOpen: true } }));
      } else if (window.__sbwEditingSelectedId === id) {
        // eslint-disable-next-line no-console
        console.log('[TEXT EDITOR] closing', id);
        window.__sbwEditingSelectedId = null;
        window.dispatchEvent(new CustomEvent('sbw:editing-change', { detail: { id, isOpen: false } }));
      }
    }
  }, [showEditor, id]);

  useEffect(() => {
    const handler = (e) => {
      const targetId = e?.detail?.id;
      // eslint-disable-next-line no-console
      console.log('[TEXT EDITOR] request-edit event', { targetId, self: id });
      if (targetId === id) {
        setShowEditor(true);
      }
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('sbw:request-edit', handler);
      return () => window.removeEventListener('sbw:request-edit', handler);
    }
  }, [id]);

  // Fallback: if a global edit request is set before the event listener is attached
  useEffect(() => {
    if (typeof window !== 'undefined' && window.__sbwEditRequestId === id) {
      // eslint-disable-next-line no-console
      console.log('[TEXT EDITOR] consuming global __sbwEditRequestId', id);
      window.__sbwEditRequestId = null;
      setShowEditor(true);
    }
  }, [id]);

  // Sync editing content with prop and focus input when inline editing starts
  useEffect(() => {
    setEditingContent(content);
  }, [content]);

  // Clean up hint timeout on unmount
  useEffect(() => {
    return () => {
      if (hintTimeoutRef.current) {
        clearTimeout(hintTimeoutRef.current);
      }
    };
  }, []);

  // Cleanup: Stop audio when component unmounts
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        try {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        } catch {}
        audioRef.current = null;
      }
    };
  }, []);

  // Hide hint when element is deselected
  useEffect(() => {
    if (!isSelected) {
      setShowDoubleClickHint(false);
      if (hintTimeoutRef.current) {
        clearTimeout(hintTimeoutRef.current);
      }
    }
  }, [isSelected]);

  // New interaction model:
  // Step 1: Single click to select (no edit)
  // Step 2: Double click to edit (or press Enter)

  useEffect(() => {
    if (isInlineEditing && inlineEditRef.current) {
      // Only set cursor position when we just entered edit mode, not on clicks
      if (justEnteredEditModeRef.current) {
        // Place cursor at the end of text when entering edit mode
        inlineEditRef.current.focus();
        const length = inlineEditRef.current.value.length;
        inlineEditRef.current.setSelectionRange(length, length);
        justEnteredEditModeRef.current = false;
      }
    }
  }, [isInlineEditing]);

  // Reset first focus flag when exiting edit mode
  useEffect(() => {
    if (!isInlineEditing) {
      isFirstFocusRef.current = true;
    }
  }, [isInlineEditing]);

  const handleUpdate = () => {
    setShowEditor(true);
  };

  const handleGenerate = async () => {
    if (isGenerating) return;
    try {
      // Pre-check credit balance (3 credits required)
      const hasCredits = await checkCredits?.(3);
      if (!hasCredits) {
        notify.error('Not enough credits to generate narration (needs 3)', { toastId: 'audio-generate-credits' });
        return;
      }

      setIsGenerating(true);
      onChange?.({ audio: { status: 'pending' } });
      notify.info('Generating audio...', { toastId: 'audio-generate-start' });
      const resp = await generateTextAudio(content || '');
      const url = resp?.url;
      if (!url) {
        onChange?.({ audio: { status: 'error' } });
        notify.error('Audio generation failed', { toastId: 'audio-generate-error' });
        return;
      }
      onChange?.({ audio: { status: 'ready', url, updatedAt: Date.now() } });
      notify.success('Audio ready', { toastId: 'audio-generate-success' });
      // Deduct 3 credits on success
      try { await updateCredits?.(-3); } catch {}
      // Preload to capture duration
      try {
        const a = new Audio(url);
        a.addEventListener('loadedmetadata', () => {
          onChange?.({ audio: { status: 'ready', url, durationSec: Math.round(a.duration || 0), updatedAt: Date.now() } });
        }, { once: true });
      } catch {}
    } catch (e) {
      onChange?.({ audio: { status: 'error' } });
      notify.error('Audio generation error', { toastId: 'audio-generate-error' });
    } finally {
      setIsGenerating(false);
    }
  };

  const handlePlayPause = async (audioUrl) => {
    if (!audioUrl) return;
    try {
      if (!audioRef.current) {
        audioRef.current = new Audio(audioUrl);
        audioRef.current.addEventListener('ended', () => { setIsPlaying(false); setProgress(0); });
        audioRef.current.addEventListener('timeupdate', () => {
          const a = audioRef.current;
          if (!a) return;
          const d = a.duration || 0;
          if (d > 0) {
            setProgress(Math.min(1, Math.max(0, a.currentTime / d)));
            setElapsedSec(Math.round(a.currentTime));
          }
        });
        audioRef.current.addEventListener('loadedmetadata', () => {
          const a = audioRef.current;
          if (!a) return;
          const dur = Math.round(a.duration || 0);
          if (!audio?.durationSec && dur > 0) {
            onChange?.({ audio: { ...(audio || {}), status: 'ready', url: audioUrl, durationSec: dur, updatedAt: Date.now() } });
          }
        });
      }
      const a = audioRef.current;
      if (isPlaying) {
        a.pause();
        setIsPlaying(false);
      } else {
        // If source changed, recreate
        if (a.src !== audioUrl) {
          a.pause();
          audioRef.current = new Audio(audioUrl);
          audioRef.current.addEventListener('ended', () => { setIsPlaying(false); setProgress(0); });
          audioRef.current.addEventListener('timeupdate', () => {
            const aa = audioRef.current;
            if (!aa) return;
            const d = aa.duration || 0;
            if (d > 0) {
              setProgress(Math.min(1, Math.max(0, aa.currentTime / d)));
              setElapsedSec(Math.round(aa.currentTime));
            }
          });
          audioRef.current.addEventListener('loadedmetadata', () => {
            const aa = audioRef.current;
            if (!aa) return;
            const dur = Math.round(aa.duration || 0);
            if (!audio?.durationSec && dur > 0) {
              onChange?.({ audio: { ...(audio || {}), status: 'ready', url: audioUrl, durationSec: dur, updatedAt: Date.now() } });
            }
          });
        }
        await audioRef.current.play();
        setIsPlaying(true);
        setProgress(0);
      }
    } catch {
      setIsPlaying(false);
    }
  };

  const fmt = (sec) => {
    if (!sec && sec !== 0) return '';
    const s = Math.max(0, Math.round(sec));
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${String(r).padStart(2, '0')}`;
  };

  const handleSeek = (clientX, trackEl) => {
    if (!audioRef.current || !trackEl) return;
    const rect = trackEl.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const a = audioRef.current;
    const d = a.duration || 0;
    if (d > 0) {
      a.currentTime = d * ratio;
      setProgress(ratio);
      setElapsedSec(Math.round(a.currentTime));
    }
  };

  const handleDownload = async () => {
    if (!audio?.url) return;
    try {
      const filename = `${(content || 'narration').slice(0, 30).replace(/[^a-z0-9]/gi, '-').toLowerCase()}.mp3`;
      await downloadAudioFile(audio.url, filename);
      notify.success('Audio downloaded', { toastId: 'audio-download-success' });
    } catch (error) {
      console.error('Download failed:', error);
      notify.error('Failed to download audio', { toastId: 'audio-download-error' });
    }
  };

  // Create container styles that enable vertical alignment
  const containerStyle = {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems:
      style.verticalAlign === 'middle'
        ? 'center'
        : style.verticalAlign === 'bottom'
        ? 'flex-end'
        : 'flex-start',
    padding: '8px',
    overflow: 'hidden',
    // In the design canvas we want dragging to move the whole text block,
    // not select individual characters. But when editing, allow normal text selection.
    userSelect: isInlineEditing ? 'auto' : 'none',
    cursor: isInlineEditing ? 'text' : 'move',
  };

  // Create text styles separate from container styles
  const textStyle = {
    width: '100%',
    fontFamily: style.fontFamily || 'Arial',
    fontSize: style.fontSize || '16px',
    lineHeight: style.lineHeight || '1',
    color: style.color || '#000000',
    textAlign: style.textAlign || 'left',
    fontWeight: style.fontWeight || 'normal',
    fontStyle: style.fontStyle || 'normal',
    textDecoration: style.textDecoration || 'none',
    // Allow drag to start even when clicking directly on text
    pointerEvents: 'none',
    // Preserve line breaks and whitespace
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
  };

  return (
    <>
      <BaseElement
        id={id}
        type="text"
        position={position}
        size={size}
        zIndex={zIndex}
        isSelected={isSelected}
        isInlineEditing={isInlineEditing}
        onSelect={onSelect}
        onMove={onMove}
        onResize={onResize}
        onDelete={onDelete}
        onDuplicate={onDuplicate}
        onZIndexChange={onZIndexChange}
        containerRef={containerRef}
      >
        <div
          style={{ position: 'relative', width: '100%', height: '100%' }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => {
            setIsHovered(false);
            // Hide hint when mouse leaves
            setShowDoubleClickHint(false);
            if (hintTimeoutRef.current) {
              clearTimeout(hintTimeoutRef.current);
            }
          }}
          onDoubleClick={(e) => {
            // Double-click enters edit mode
            if (!isInlineEditing) {
              console.log('[TextElement] Entering edit mode (double-click)');
              e.stopPropagation();
              justEnteredEditModeRef.current = true;
              setIsInlineEditing(true);
            }
          }}
          onMouseMove={(e) => {
            // Show hint when hovering over selected element
            if (isSelected && !isInlineEditing) {
              setHintPosition({ x: e.clientX, y: e.clientY });
              
              // Clear existing timeout
              if (hintTimeoutRef.current) {
                clearTimeout(hintTimeoutRef.current);
              }
              
              // Show hint
              setShowDoubleClickHint(true);
              
              // Auto-hide hint after 3 seconds of inactivity
              hintTimeoutRef.current = setTimeout(() => {
                setShowDoubleClickHint(false);
              }, 3000);
            }
          }}
          onMouseDown={(e) => {
            // Prevent drag behavior when editing
            if (isInlineEditing) {
              e.stopPropagation();
            }
          }}
        >
          <div style={containerStyle}>
            {isInlineEditing ? (
               <textarea
                 ref={inlineEditRef}
                 value={editingContent}
                 onChange={(e) => setEditingContent(e.target.value)}
                 onBlur={() => {
                   setIsInlineEditing(false);
                   if (editingContent !== content) {
                     onChange?.({ content: editingContent });
                   }
                 }}
                 onKeyDown={(e) => {
                   if (e.key === 'Escape') {
                     e.preventDefault();
                     setIsInlineEditing(false);
                     setEditingContent(content);
                   }
                 }}
                 onClick={(e) => {
                   // Let textarea handle click positioning naturally
                   e.stopPropagation();
                 }}
                 onMouseDown={(e) => {
                   // Prevent parent drag behavior, let textarea handle selection
                   e.stopPropagation();
                 }}
                style={{
                  fontFamily: style.fontFamily || 'Arial',
                  fontSize: style.fontSize || '16px',
                  lineHeight: style.lineHeight || '1',
                  color: style.color || '#000000',
                  textAlign: style.textAlign || 'left',
                  fontWeight: style.fontWeight || 'normal',
                  fontStyle: style.fontStyle || 'normal',
                  textDecoration: style.textDecoration || 'none',
                  width: '100%',
                  height: '100%',
                  background: 'transparent',
                  pointerEvents: 'auto',
                  boxSizing: 'border-box',
                  resize: 'none',
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  border: 'none',
                  outline: 'none',
                }}
              />
            ) : (
              <div style={textStyle}>{content || 'Click to edit text'}</div>
            )}
          </div>
          {/* Double-click hint tooltip */}
          {showDoubleClickHint && isSelected && !isInlineEditing && (
            <div
              style={{
                position: 'fixed',
                left: `${hintPosition.x + 10}px`,
                top: `${hintPosition.y + 10}px`,
                pointerEvents: 'none',
                zIndex: 50000,
              }}
              className="animate-in fade-in"
            >
              <div className="bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap shadow-lg">
                Double click to edit/write text
              </div>
            </div>
          )}

          {(isHovered || isSelected || isPlaying) && (
            <div
              role="group"
              aria-label="Naration player"
              data-role="control-button"
              className="flex items-center gap-1 rounded-full bg-white/90 backdrop-blur border border-gray-200 shadow-md p-1"
              style={{ position: 'absolute', top: '-26px', left: '0px', zIndex: 20000, pointerEvents: 'auto' }}
              onPointerDownCapture={(e) => { e.preventDefault(); e.stopPropagation(); if (typeof window !== 'undefined') window.__sbwSuppressCanvasClick = true; }}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (typeof window !== 'undefined') window.__sbwSuppressCanvasClick = true; }}
            >
              <div className="flex items-center gap-1 text-gray-600 px-1">
                <MusicalNoteIcon className="w-3.5 h-3.5" />
                <span className="text-[10px] font-medium">Naration</span>
              </div>

              {/* Play/Pause button */}
              <button
                type="button"
                title={isPlaying ? 'Pause' : 'Play'}
                disabled={isGenerating || !audio?.url}
                onClick={(e) => { e.stopPropagation(); handlePlayPause(audio?.url || undefined); }}
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                className="text-gray-800 disabled:opacity-50"
                data-role="control-button"
              >
                {isPlaying ? (
                  <PauseCircleIcon className="w-5 h-5" />
                ) : (
                  <PlayCircleIcon className="w-5 h-5" />
                )}
              </button>

              {/* Progress/seek bar */}
              <div
                className="relative h-2 w-28 bg-gray-200 rounded-full cursor-pointer ml-2"
                onMouseDown={(e) => { e.stopPropagation(); handleSeek(e.clientX, e.currentTarget); }}
                onClick={(e) => { e.stopPropagation(); handleSeek(e.clientX, e.currentTarget); }}
                title="Seek"
              >
                <div
                  className="absolute top-0 left-0 h-2 bg-indigo-500 rounded-full"
                  style={{ width: `${Math.round(progress * 100)}%` }}
                />
                <div
                  className="absolute top-1/2 w-3 h-3 bg-indigo-600 rounded-full shadow"
                  style={{ left: `${Math.round(progress * 100)}%`, transform: 'translate(-50%, -50%)' }}
                />
              </div>

              {/* Time */}
              <div className="text-[10px] tabular-nums text-gray-700 w-16 text-right">
                {audio?.url ? `${fmt(elapsedSec)} / ${fmt(audio?.durationSec || 0)}` : '—:— / —:—'}
              </div>

              {/* Download button */}
              {audio?.url && (
                <button
                  type="button"
                  data-role="control-button"
                  className="text-gray-700 hover:text-gray-900 disabled:opacity-40"
                  onClick={(e) => { e.stopPropagation(); handleDownload(); }}
                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  title="Download audio"
                >
                  <ArrowDownTrayIcon className="w-4 h-4" />
                </button>
              )}

              {/* Generate/Regenerate */}
              <button
                type="button"
                data-role="control-button"
                className="px-2 py-0.5 text-[10px] rounded-full bg-indigo-600 text-white disabled:opacity-50 whitespace-nowrap"
                disabled={isGenerating}
                onClick={(e) => { e.stopPropagation(); setShowNarrationConfirm(true); }}
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                title={audio?.url ? 'Regenerate narration' : 'Generate narration'}
              >
                {isGenerating ? 'Generating…' : (audio?.url ? 'Regenerate' : 'Generate')}
              </button>
            </div>
          )}
        </div>
      </BaseElement>

      <NarrationConfirmModal
        isOpen={showNarrationConfirm}
        isRegenerate={!!audio?.url}
        cost={3}
        balance={credits}
        onCancel={() => setShowNarrationConfirm(false)}
        onConfirm={async () => {
          setShowNarrationConfirm(false);
          await handleGenerate();
        }}
      />

      <TextEditor
        isOpen={showEditor}
        onClose={() => setShowEditor(false)}
        content={content}
        style={style}
        onSave={(updates) => {
          console.log('[TextElement] Saving updates:', updates);
          onChange?.({
            content: updates.content,
            style: updates.style,
          });
          setShowEditor(false);
        }}
      />
    </>
  );
}
